import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_CRUD_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ConditionVariable} from '../conditions/visual/workflow-condition-model';
import {mappingUsage, sourceStage, VariableUsage, WorkflowMapping} from './workflow-variables';

export interface WorkflowVariablesComponent extends Translatable {}

/** Presents mapped variables with readable source chips, usage links and availability hints. */
@Component({selector: 'app-workflow-variables', templateUrl: './workflow-variables.component.html',
  styleUrls: ['./workflow-variables.component.css']})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class WorkflowVariablesComponent {
  @Input() workflow: any;
  @Input() descriptors: ConditionVariable[] = [];
  @Output() edit = new EventEmitter<string>();
  @Output() remove = new EventEmitter<string>();
  @Output() openStage = new EventEmitter<number>();
  public query = '';
  private usageWorkflow: any;
  private usageCache = new Map<string, VariableUsage>();

  /** Injects localized labels for the variable overview. */
  public constructor(@Optional() private translationService: TranslationService) {}

  /** Resolves the same label and value type used in condition previews. */
  public descriptor(variable: WorkflowMapping): ConditionVariable { return this.descriptors.find(item => item.name === variable.name); }

  /** Filters mappings without changing their stored order. */
  public get matches(): WorkflowMapping[] {
    const terms = this.query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    return (this.workflow?.variables || []).filter(variable => {
      const descriptor = this.descriptor(variable);
      return terms.every(term => `${variable.name} ${descriptor?.label || ''} ${descriptor?.detail || ''}`.toLocaleLowerCase().includes(term));
    });
  }

  /** Finds actual step references without substring matches in literal values. */
  public usages(variable: WorkflowMapping): number[] { return this.usage(variable.name).indices; }

  /** Keeps usage uncertainty visible for unsupported legacy expressions. */
  public get unknownUsage(): boolean { return this.usage('').unknown; }

  /** Reuses parsed usage results while only filters or hover state change. */
  private usage(name: string): VariableUsage {
    if (this.usageWorkflow !== this.workflow) { this.usageWorkflow = this.workflow; this.usageCache.clear(); }
    if (!this.usageCache.has(name)) { this.usageCache.set(name, mappingUsage(this.workflow, name)); }
    return this.usageCache.get(name);
  }

  /** Describes the earliest configured source step. */
  public availability(variable: WorkflowMapping): string {
    if (variable.target.type === 'survey') { return this._('Von Beginn an verfügbar'); }
    const index = sourceStage(this.workflow, variable.target);
    return index < 0 ? this._('Quellfragebogen ist keinem Schritt zugeordnet') : this._('Nach Schritt') + ' ' + (index + 1);
  }

  /** Keeps unchanged rows stable while workflow form values update. */
  public trackVariable(index: number, variable: WorkflowMapping): string { return variable.name; }
}
